/**
 * Logger Types
 * TypeScript types for the logging system
 * 
 * @module lib/logger/types
 */

// =============================================================================
// Log Levels
// =============================================================================

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'fatal';

export const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
    fatal: 4,
};

// =============================================================================
// Log Entry
// =============================================================================

export interface LogContext {
    /** User ID (if authenticated) */
    userId?: string;
    /** Session ID */
    sessionId?: string;
    /** Request ID (for tracing) */
    requestId?: string;
    /** Component or module name */
    component?: string;
    /** Additional metadata */
    [key: string]: unknown;
}

export interface LogEntry {
    /** Log level */
    level: LogLevel;
    /** Log message */
    message: string;
    /** Timestamp in ISO format */
    timestamp: string;
    /** Context information */
    context: LogContext;
    /** Additional data */
    data?: Record<string, unknown>;
    /** Error object (if logging an error) */
    error?: {
        name: string;
        message: string;
        stack?: string;
        code?: string;
    };
    /** Environment (development/production) */
    environment: string;
}

// =============================================================================
// Transport Interface
// =============================================================================

export interface LogTransport {
    /** Transport name */
    name: string;
    /** Minimum log level for this transport */
    minLevel: LogLevel;
    /** Log a single entry */
    log(entry: LogEntry): void | Promise<void>;
    /** Flush any pending logs */
    flush?(): Promise<void>;
}

// =============================================================================
// Logger Configuration
// =============================================================================

export interface LoggerConfig {
    /** Minimum log level */
    minLevel: LogLevel;
    /** Log transports */
    transports: LogTransport[];
    /** Default context applied to all logs */
    defaultContext?: LogContext;
    /** Whether to include stack traces */
    includeStackTrace?: boolean;
    /** PII fields to scrub from logs */
    piiFields?: string[];
    /** Whether logging is enabled */
    enabled?: boolean;
}

// =============================================================================
// Logger Interface
// =============================================================================

export interface ILogger {
    debug(message: string, data?: Record<string, unknown>): void;
    info(message: string, data?: Record<string, unknown>): void;
    warn(message: string, data?: Record<string, unknown>): void;
    error(message: string, error?: Error | unknown, data?: Record<string, unknown>): void;
    fatal(message: string, error?: Error | unknown, data?: Record<string, unknown>): void;

    /** Create a child logger with additional context */
    child(context: LogContext): ILogger;

    /** Set context for this logger instance */
    setContext(context: LogContext): void;

    /** Flush all transports */
    flush(): Promise<void>;
}
